import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash } from "@phosphor-icons/react";
import PhotoAttacher, { AttachedMedia } from "@/components/vansha/PhotoAttacher";
import { CurveWeaveGraphic } from "@/components/vansha/Graphics";

export default function Timeline() {
  const [items, setItems] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", event_date: "", category: "milestone", memory_ids: [] });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const r = await api.get("/timeline");
    setItems(r.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/timeline", form);
      toast.success("Moment added to your timeline.");
      setForm({ title: "", description: "", event_date: "", category: "milestone", memory_ids: [] });
      setAdding(false);
      load();
    } catch { toast.error("Could not save."); }
  };

  const del = async (id) => {
    await api.delete(`/timeline/${id}`);
    toast.success("Removed.");
    load();
  };

  return (
    <div className="space-y-8" data-testid="timeline-page">
      <header className="relative overflow-hidden vansha-card-tinted-sand p-8 sm:p-10 flex items-end justify-between flex-wrap gap-4">
        <div className="absolute right-0 top-0 opacity-90 hidden lg:block pointer-events-none">
          <CurveWeaveGraphic width={420} height={200} />
        </div>
        <div className="relative z-10">
          <p className="overline text-terracotta mb-2">Your family story</p>
          <h1 className="font-serif font-semibold text-4xl sm:text-5xl lg:text-6xl leading-none">
            Time<em className="text-terracotta font-normal italic">line</em>
          </h1>
          <p className="text-ink-700 mt-3 leading-relaxed max-w-xl">Every milestone, scan, and small wonder — in one unbroken thread.</p>
        </div>
        <button onClick={() => setAdding(!adding)} className="btn-primary relative z-10" data-testid="add-event-btn">
          <Plus weight="thin" size={18} /> Add moment
        </button>
      </header>

      {adding && (
        <form onSubmit={submit} className="vansha-card p-6 space-y-5" data-testid="add-event-form">
          <div className="grid sm:grid-cols-2 gap-5">
            <div><label className="overline mb-2 block">Title</label><input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="input-line" placeholder="First heartbeat" data-testid="event-title" /></div>
            <div><label className="overline mb-2 block">Date</label><input type="date" required value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} className="input-line" data-testid="event-date" /></div>
          </div>
          <div><label className="overline mb-2 block">Description</label><textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="input-line" rows="2" placeholder="A small note for the future." data-testid="event-description" /></div>
          <div><label className="overline mb-2 block">Category</label>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="input-line bg-transparent" data-testid="event-category">
              <option value="milestone">Milestone</option><option value="scan">Scan</option><option value="appointment">Appointment</option>
              <option value="memory">Memory</option><option value="birth">Birth</option><option value="custom">Other</option>
            </select>
          </div>
          <div>
            <PhotoAttacher value={form.memory_ids} onChange={(ids) => setForm({ ...form, memory_ids: ids })} label="Attach photos" />
          </div>
          <div className="flex gap-3">
            <button type="button" onClick={() => setAdding(false)} className="btn-ghost" data-testid="event-cancel">Cancel</button>
            <button type="submit" className="btn-primary" data-testid="event-submit">Save moment</button>
          </div>
        </form>
      )}

      {loading ? <p className="text-ink-500">Loading…</p> : items.length === 0 ? (
        <div className="vansha-card p-12 text-center text-ink-500" data-testid="timeline-empty">
          <p className="font-serif text-2xl text-ink-900 mb-2">Your story begins here.</p>
          <p>Add your first moment — the positive test, first scan, or any small wonder.</p>
        </div>
      ) : (
        <div className="relative">
          <div className="absolute left-6 sm:left-32 top-2 bottom-2 w-px timeline-line" />
          <div className="space-y-8">
            {items.map((it) => (
              <div key={it.id} className="relative pl-16 sm:pl-44" data-testid={`timeline-event-${it.id}`}>
                <div className="absolute left-[18px] sm:left-[121px] top-3 timeline-dot" />
                <div className="hidden sm:block absolute left-0 top-2 w-28 text-right">
                  <p className="font-serif text-2xl text-ink-900">{new Date(it.event_date).getDate()}</p>
                  <p className="overline">{new Date(it.event_date).toLocaleString("en-IN", { month: "short", year: "numeric" })}</p>
                </div>
                <div className="vansha-card p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <p className="overline text-saffron mb-1 sm:hidden">{new Date(it.event_date).toLocaleDateString()}</p>
                      <p className="overline mb-1 hidden sm:block">{it.category}</p>
                      <h3 className="font-serif text-2xl">{it.title}</h3>
                      {it.description && <p className="text-ink-700 mt-2">{it.description}</p>}
                      <AttachedMedia ids={it.memory_ids} />
                    </div>
                    <button onClick={() => del(it.id)} className="btn-ghost p-2" data-testid={`delete-${it.id}`}><Trash weight="thin" size={18} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
